import { Add, ChevronRight } from "@mui/icons-material";
import { Box, Divider, Grid, InputLabel } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DepartureTimeExp, TicketType, TransportationType, ValidTextRegExp } from "../../../../Constant";
import { formatDepartureDate, getTrainStationOptions } from "../../../../Utils";
import { getConfirmation } from "../../../../components/my-confirmation-dialog/MyConfirmationDialog";
import { MyDatePicker } from "../../../../components/my-date-picker/MyDatePicker";
import { MyDropdown } from "../../../../components/my-dropdown/MyDropdown";
import { MyTextField } from "../../../../components/my-text-field/MyTextField";
import { MyTimePicker } from "../../../../components/my-time-picker/MyTimePicker";
import MyContext from "../../../../contexts/MyContext";
import { TrainTheme } from "../../../../infrastructure/TrainThemeProvider";
import { FormWrapper } from "../../FormWrapper";
import { AddAccommodationReceiptDialogNew } from "../dialogs/AddAccommodationReceiptDialogNew";
import { AddFoodReceiptDialogNew } from "../dialogs/AddFoodReceiptDialogNew";
import { AddTransportReceiptDialogNew } from "../dialogs/AddTransportReceiptDialogNew";
import ClaimApiService from "../../../../adapters/xhr/ClaimApiService";

export const TicketForm2New = (props) => {
  const { t, i18n } = useTranslation();
  const myContext = useContext(MyContext);
  const [addFoodReceipt, setAddFoodReceipt] = useState(false);
  const [addAccommodationReceipt, setAddAccommodationReceipt] = useState(false);
  const [addTransportReceipt, setAddTransportReceipt] = useState(false);
  const [departureStationValueErr, setDepartureStationValueErr] = useState("");
  const [departureDateValueErr, setDepartureDateValueErr] = useState("");
  const [departureTimeValueErr, setDepartureTimeValueErr] = useState(null);
  const [arrivalStationValueErr, setArrivalStationValueErr] = useState("");
  const [commentValueErr, setCommentValueErr] = useState("");
  const [commentValueRequired, setCommentValueRequired] = useState(false);

  const validate = async () => {
    let isValid = true;
    if (!myContext.departureStation) {
      setDepartureStationValueErr("required");
      isValid = false;
    } else {
      setDepartureStationValueErr("");
    }
    if (!myContext.arrivalStation) {
      setArrivalStationValueErr("required");
      isValid = false;
    } else {
      setArrivalStationValueErr("");
    }
    if (!myContext.departureDate || myContext.departureDate.trim() === "") {
      setDepartureDateValueErr("required");
      isValid = false;
    } else {
      setDepartureDateValueErr("");
    }
    if (!myContext.departureTime) {
      setDepartureTimeValueErr("required");
      isValid = false;
    } else {
      setDepartureTimeValueErr("");
    }
    if (commentValueRequired && !myContext.otherComments?.trim()) {
      setCommentValueErr("required");
      isValid = false;
    } else {
      setCommentValueErr("");
    }
    if (isValid) {
      const ticketInfo = myContext.ticketInfo;
      const departureDate = new Date(`${formatDepartureDate(myContext.departureDate, myContext.departureTime).toISOString().slice(0, -1)}`);

      let validTicket = true;

      if (myContext.ticketType === TicketType.SINGLE) {
        validTicket = !ticketInfo?.activations.every((item) => new Date(item.validFrom) < departureDate || new Date(item.validFrom) > departureDate);
      } else if (myContext.ticketType === TicketType.COMMUTER) {
        validTicket = !ticketInfo?.activations.every((item) => new Date(item.validFrom) > departureDate || new Date(item.validTo) < departureDate);
      }

      if (!validTicket) {
        const confirmed = await getConfirmation({
          title: t("info"),
          message: t("ticket_validity_does_not_match_departure_date"),
          theme: TrainTheme,
        });
        if (confirmed) validTicket = true;
      }

      if (validTicket) {
        if (!myContext.biljettnummerValue?.trim()) myContext.changeActiveSlide((myContext.activeSlide += 1));
        else {
          const duplicate = await duplicateCheck();
          if (duplicate) {
            const confirmed = await getConfirmation({
              title: t("info"),
              message: t("duplicate_claim_message"),
              theme: TrainTheme,
            });
            if (confirmed) myContext.changeActiveSlide((myContext.activeSlide += 1));
          } else myContext.changeActiveSlide((myContext.activeSlide += 1));
        }
      }
    }
  };

  const duplicateCheck = async () => {
    const result = await ClaimApiService.checkDuplicate({
      departureStationId: myContext.departureStation?.key,
      arrivalStationId: myContext.arrivalStation?.key,
      departureDate: `${formatDepartureDate(myContext.departureDate, myContext.departureTime).toISOString()}`,
      ticketNumber: myContext.biljettnummerValue?.trim(),
    });
    return result?.isSuccess && result?.data;
  };

  const handleChangeDepartureTime = (value) => {
    let departureTimeReg = new RegExp(DepartureTimeExp);
    if (departureTimeReg.test(value)) {
      myContext.changeDepartureTime(value);
    } else myContext.changeDepartureTime(null);
  };

  useEffect(() => {
    if ((myContext.transportationType === TransportationType.OTHERS && myContext.transportReceipt.length > 0) || myContext.foodReceipt.length > 0 || myContext.accommodationReceipt.length > 0 || (myContext.transportationType === TransportationType.USEOWNCAR && myContext.numberOfKmDriven) || !myContext.biljettnummerValue?.trim()) {
      setCommentValueRequired(true);
    } else {
      setCommentValueRequired(false);
      myContext.changeOtherComments("");
    }
  }, [myContext.biljettnummerValue, myContext.transportReceipt, myContext.foodReceipt, myContext.accommodationReceipt, myContext.transportationType, myContext.numberOfKmDriven]);

  const commentFieldLabel = () => {
    if (myContext.biljettnummerValue?.trim()) return t("other_comments");
    else return t("other_comments_without_ticket");
  };

  return (
    <MyContext.Consumer>
      {(context) => (
        <React.Fragment>
          <FormWrapper onContinue={validate} heading={`${t("travel_info")}`}>
            <p id="required-description" className="margin bottom-0" style={{ marginTop: "0.5rem", width: "100%" }}>
              <Box component="small" color="error.main">
                *
              </Box>
              <small> {t("required_field")}</small>
            </p>
            <InputLabel style={{ marginTop: "1.25rem", fontWeight: "bold", color: "#222", width: "100%" }} className="margin bottom-0" htmlFor="departureStation">{`${t("departure_station")}`}</InputLabel>
            <MyDropdown noOptionsText={t("no_options")} required={true} helperText={t(departureStationValueErr)} onChange={(e, value) => context.changeDepartureStation(value)} label={t("departure_station")} value={context.departureStation} options={getTrainStationOptions(context.trainStations).filter((option) => option.key !== context.arrivalStation?.key)} />
            <InputLabel style={{ marginTop: "1.25rem", fontWeight: "bold", color: "#222", width: "100%" }} className="margin bottom-0" htmlFor="arrivalStation">{`${t("arrival_station")}`}</InputLabel>
            <MyDropdown noOptionsText={t("no_options")} required={true} helperText={t(arrivalStationValueErr)} onChange={(e, value) => context.changeArrivalStation(value)} label={t("arrival_station")} value={context.arrivalStation} options={getTrainStationOptions(context.trainStations).filter((option) => option.key !== context.departureStation?.key)} />
            <InputLabel style={{ marginTop: "1.25rem", fontWeight: "bold", color: "#222", width: "100%" }} className="margin bottom-0" htmlFor="departureDate">{`${t("departure_date")}`}</InputLabel>
            <MyDatePicker required={true} error={departureDateValueErr !== ""} label={t("departure_date")} value={context.departureDate} onAccept={(date) => context.changeDepartureDate(date?.toString())} helperText={t(departureDateValueErr)} maxDate={new Date()} minDate={new Date(2021, 11, 12)} placeholder={t("date_format")} cancelLabel={t("cancel")} okLabel={t("ok")} locale={i18n.language} views={["day"]} />
            <InputLabel style={{ marginTop: "1.25rem", fontWeight: "bold", color: "#222", width: "100%" }} className="margin bottom-0" htmlFor="departureTime">{`${t("departure_time")}`}</InputLabel>
            {context.departureTimeOptions == null || context.departureTimeOptions.length == 0 ? <MyTimePicker required={true} onAccept={(date) => context.changeDepartureTime(date?.toString())} value={context.departureTime} label={t("departure_time")} helperText={t(departureTimeValueErr)} cancelLabel={t("cancel")} okLabel={t("ok")} /> : <MyDropdown noOptionsText={t("no_options")} required={true} helperText={t(departureTimeValueErr)} onChange={(e, time) => handleChangeDepartureTime(time !== null ? time.value : null)} label={t("departure_time")} freeSolo={new Date(context.departureDate).getDate() >= new Date().getDate()} placeholder={`HH:mm`} onFreeSolo={(e) => handleChangeDepartureTime(e.target.value)} value={context.departureTime} options={context.departureTimeOptions} />}
            <h5 style={{ width: "100%" }}>{t("do_you_have_additional_costs")}</h5>
            <Divider style={{ width: "100%" }} />
            {!context.distance?.isShortTrain && (
              <>
                <Grid container onClick={(e) => setAddFoodReceipt(true)} className="selector" alignItems="center" width={"100%"}>
                  <Add style={{ marginRight: "1rem" }} />
                  <span style={{ fontSize: "1rem" }}>{t("add_food_expenses")}</span>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                    <Box component="span" fontSize={"1rem"} color="primary.main">
                      {context.foodTotalCost && `${context.foodTotalCost} ${t("kr")}`}
                    </Box>
                    <ChevronRight />
                  </div>
                </Grid>
                <Divider style={{ width: "100%" }} />
              </>
            )}
            <Grid container onClick={(e) => setAddTransportReceipt(true)} className="selector" alignItems="center" width={"100%"}>
              <Add style={{ marginRight: "1rem" }} />
              <span style={{ fontSize: "1rem" }}>{t("add_transport_expenses")}</span>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                <Box component="span" fontSize={"1rem"} color="primary.main">
                  {context.transportationType == TransportationType.TAXI && context.transportTotalCost && `${context.transportTotalCost} ${t("kr")}`}
                  {context.transportationType == TransportationType.OTHERS && context.transportTotalCost && `${context.transportTotalCost} ${t("kr")}`}
                  {context.transportationType == TransportationType.USEOWNCAR && context.numberOfKmDriven && `${context.numberOfKmDriven} ${t("km")}`}
                </Box>
                <ChevronRight />
              </div>
            </Grid>
            {!context.distance?.isShortTrain && (
              <>
                <Divider style={{ width: "100%" }} />
                <Grid container onClick={(e) => setAddAccommodationReceipt(true)} className="selector" alignItems="center" width={"100%"}>
                  <Add style={{ marginRight: "1rem" }} />
                  <span style={{ fontSize: "1rem" }}>{t("add_accommodation_expenses")}</span>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                    <Box component="span" fontSize={"1rem"} color="primary.main">
                      {context.accommodationTotalCost && `${context.accommodationTotalCost} ${t("kr")}`}
                    </Box>
                    <ChevronRight />
                  </div>
                </Grid>
              </>
            )}
            <Divider style={{ width: "100%" }} />
            {commentValueRequired && (
              <MyTextField
                inputLabelStyles={{
                  sx: {
                    whiteSpace: { xs: "wrap", sm: "break-spaces" },
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      whiteSpace: "nowrap",
                    },
                  },
                }}
                helperText={t(commentValueErr)}
                required={commentValueRequired}
                regExp={ValidTextRegExp}
                value={context.otherComments}
                onChange={context.changeOtherComments}
                label={commentFieldLabel()}
                multiline={true}
                rows={4}
                style={{ marginBottom: "0" }}
              />
            )}
          </FormWrapper>
          <AddFoodReceiptDialogNew open={addFoodReceipt} onClose={(e) => setAddFoodReceipt(false)} />
          <AddTransportReceiptDialogNew open={addTransportReceipt} onClose={(e) => setAddTransportReceipt(false)} />
          <AddAccommodationReceiptDialogNew open={addAccommodationReceipt} onClose={(e) => setAddAccommodationReceipt(false)} />
        </React.Fragment>
      )}
    </MyContext.Consumer>
  );
};
